import type { UIMessage } from "../../types/chat";
import { cn, formatMessageTime } from "../../lib/utils";
import DocumentTextLinear from "~icons/solar/document-text-linear";

interface UserMessageBubbleProps {
  message: UIMessage;
  userName: string;
  avatarUrl?: string;
}

/**
 * Display-only transform: replaces underscores with spaces inside @mention tokens.
 * e.g. "@Middle_Go_Lang_Developer" → "@Middle Go Lang Developer"
 * The stored/sent content is never modified — this only affects rendering.
 */
function formatMessageForDisplay(content: string): string {
  return content.replace(
    /@(\S+)/g,
    (_match, word: string) => `@${word.replaceAll("_", " ")}`
  );
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export function UserMessageBubble({
  message,
  userName,
  avatarUrl,
}: UserMessageBubbleProps) {
  const visibleDocuments = message.documents?.filter((d) => !d.silent) ?? [];

  return (
    <div className="flex items-end justify-end gap-3">
      {/* Clean light grey bubble, no shadow (v3) */}
      <div className="max-w-[88%] min-w-0 rounded-[20px] rounded-br-[4px] border border-black/[0.05] bg-surface-3 p-4 text-sm leading-relaxed text-ink shadow-none sm:max-w-[80%]">
        {visibleDocuments.length > 0 && (
          <div className={cn("flex flex-wrap gap-2", message.content && "mb-2.5")}>
            {visibleDocuments.map((doc, index) => {
              const fileName =
                doc.file_name ??
                (typeof doc.document_url === "string"
                  ? doc.document_url.split("/").pop() || "Document"
                  : "Document");
              return (
                <span
                  key={doc.document_url || index}
                  className="flex items-center gap-2 rounded-xl border border-black/[0.08] bg-white/90 px-3 py-1.5 text-xs font-medium text-ink shadow-xs"
                >
                  <DocumentTextLinear
                    aria-hidden="true"
                    className="size-4 shrink-0 text-ink-muted"
                  />
                  <span className="max-w-[160px] truncate">{fileName}</span>
                </span>
              );
            })}
          </div>
        )}
        {message.content && (
          <p className="whitespace-pre-line break-words">
            {formatMessageForDisplay(message.content)}
          </p>
        )}
        {message.timestamp && (
          <span className="mt-2 block text-right text-xs text-ink-faint">
            {formatMessageTime(message.timestamp)}
          </span>
        )}
      </div>

      {/* Compact 26px avatar, bottom right (v3) */}
      <div
        className="relative mb-0.5 size-[26px] shrink-0 overflow-hidden rounded-full bg-surface-2 shadow-[0px_0px_0px_2px_white,0px_1px_2px_0px_rgba(0,0,0,0.06)]"
        aria-hidden="true"
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="size-full object-cover" />
        ) : (
          <span className="flex size-full items-center justify-center text-[10px] font-bold text-ink-muted">
            {initialsOf(userName)}
          </span>
        )}
      </div>
    </div>
  );
}
